import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionPlanDetail } from './hooks/usePlan';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  pending_approval: 'bg-amber-100 text-amber-600',
  approved: 'bg-blue-100 text-blue-600',
  active: 'bg-emerald-100 text-emerald-600',
  on_hold: 'bg-red-100 text-red-600',
  completed: 'bg-emerald-900 text-white',
  cancelled: 'bg-slate-400 text-white',
  archived: 'bg-slate-900 text-white',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản thảo',
  pending_approval: 'Chờ duyệt',
  approved: 'Đã duyệt',
  active: 'Đang triển khai',
  on_hold: 'Tạm dừng',
  completed: 'Hoàn thành',
  cancelled: 'Đã hủy',
  archived: 'Lưu trữ',
};

const typeLabels: Record<string, string> = {
  master_plan: 'Kế hoạch tổng thể',
  weekly_plan: 'Kế hoạch tuần',
  daily_plan: 'Kế hoạch ngày',
  zone_plan: 'Kế hoạch theo Zone',
  phase_plan: 'Kế hoạch theo Giai đoạn',
  rework_plan: 'Kế hoạch sửa lỗi',
  handover_plan: 'Kế hoạch bàn giao',
};

export default function PlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const { plan: item, items, isLoading, updatePlan } = useConstructionPlanDetail(id);

  const [activeTab, setActiveTab] = useState('items');

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết kế hoạch...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin kế hoạch</div>;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updatePlan({ status_code: newStatus as any });
      toast.success(`Đã cập nhật trạng thái kế hoạch sang ${statusLabels[newStatus]}`);
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'items', label: 'Hạng mục chi tiết' },
    { value: 'resources', label: 'Tài nguyên dự kiến' },
    { value: 'checklist', label: 'Checklist sẵn sàng' },
    { value: 'dependencies', label: 'Phụ thuộc công việc' },
    { value: 'schedule', label: 'Lịch trình (Schedule)' },
    { value: 'work_orders', label: 'Lệnh việc (Work Orders)' },
  ];

  return (
    <PageDetailLayout
      title={item.name}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[item.status_code] || item.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">#{item.code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-building-line" />
            {item.project?.name}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Kế hoạch thi công', path: '/construction/plan' },
        { label: item.code }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'pending_approval' && (
            <AuthButton module="construction" action="approve" variant="success" icon="ri-check-line" onClick={() => handleStatusChange('approved')}>
              Phê duyệt Kế hoạch
            </AuthButton>
          )}
          {item.status_code === 'approved' && (
            <AuthButton module="construction" action="update" variant="primary" icon="ri-play-line" onClick={() => handleStatusChange('active')}>
              Kích hoạt Kế hoạch
            </AuthButton>
          )}
          <Button variant="outline" icon="ri-edit-line">Chỉnh sửa</Button>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Thời lượng dự kiến" value={Math.ceil((new Date(item.end_date).getTime() - new Date(item.start_date).getTime()) / (1000 * 60 * 60 * 24))} icon="ri-calendar-line" variant="primary" suffix=" ngày" />
                <KPICard title="Tổng hạng mục" value={items.length} icon="ri-list-check-line" variant="info" />
                <KPICard title="Phiên bản kế hoạch" value={`v${item.version_no}`} icon="ri-git-branch-line" variant="warning" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Mô tả mục tiêu kế hoạch
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-100 italic">
                  "{item.description || 'Chưa có thông tin mục tiêu chi tiết cho kế hoạch này.'}"
                </p>

                <div className="mt-8 pt-6 border-t border-slate-50">
                  <h4 className="text-[10px] font-bold text-slate-400  mb-4 tracking-widest">Tiến độ thời gian</h4>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-bold text-slate-700  tracking-tighter">BẮT ĐẦU: {new Date(item.start_date).toLocaleDateString('vi-VN')}</span>
                    <span className="text-xs font-bold text-slate-700  tracking-tighter">KẾT THÚC: {new Date(item.end_date).toLocaleDateString('vi-VN')}</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-os-primary w-[35%]" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-node-tree text-slate-400" />
                  Định vị & Loại kế hoạch
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Phân loại</span>
                    <span className="text-sm font-bold text-slate-700">{typeLabels[item.plan_type]}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Dự án</span>
                    <span className="text-sm font-bold text-slate-700">{item.project?.name}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Người lập</span>
                    <span className="text-sm font-bold text-slate-700">{item.author?.full_name}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Ngày tạo</span>
                    <span className="text-sm font-bold text-slate-700">{new Date(item.created_at).toLocaleDateString('vi-VN')}</span>
                  </div>
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6 bg-slate-900 text-white">
                <h4 className="font-bold mb-4 flex items-center gap-2 text-os-primary">
                  <i className="ri-shield-check-line" />
                  Kiểm soát Sẵn sàng
                </h4>
                <div className="space-y-4">
                  <div className="p-3 bg-slate-800 rounded-2xl border border-slate-700">
                    <p className="text-[10px] text-slate-400 font-bold  mb-1">Checklist chuẩn bị</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-400 w-[60%]" />
                      </div>
                      <span className="text-[10px] font-bold text-emerald-400">60%</span>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-800 rounded-2xl border border-slate-700">
                    <p className="text-[10px] text-slate-400 font-bold  mb-1">Tính pháp lý</p>
                    <Badge variant="neutral" className="bg-blue-900/50 text-blue-300 font-bold text-[9px] ">ĐÃ DUYỆT BOQ</Badge>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-flag-line text-slate-400" />
                  Mốc quan trọng (Milestones)
                </h4>
                <div className="space-y-3">
                  <div className="flex gap-3 relative pl-4 border-l border-slate-100 pb-4">
                    <div className="absolute left-[-5px] top-0 w-2.5 h-2.5 rounded-full bg-os-primary" />
                    <div>
                      <p className="text-xs font-bold text-slate-900">Bắt đầu thi công</p>
                      <p className="text-[10px] text-slate-400">{new Date(item.start_date).toLocaleDateString('vi-VN')}</p>
                    </div>
                  </div>
                  <div className="flex gap-3 relative pl-4 border-l border-slate-100">
                    <div className="absolute left-[-5px] top-0 w-2.5 h-2.5 rounded-full bg-slate-200" />
                    <div>
                      <p className="text-xs font-bold text-slate-400">Bàn giao khu vực</p>
                      <p className="text-[10px] text-slate-400">{new Date(item.end_date).toLocaleDateString('vi-VN')}</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'items' && (
          <Card className="overflow-hidden border-none shadow-sm">
            <div className="p-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-700">Hạng mục công việc chi tiết ({items.length})</h3>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" icon="ri-filter-3-line">Lọc công việc</Button>
                <AuthButton module="construction" action="update" variant="outline" size="sm" icon="ri-add-line">Thêm hạng mục</AuthButton>
              </div>
            </div>
            <Table
              columns={[
                { key: 'name', title: 'Công tác thi công' },
                { key: 'location', title: 'Định vị (Zone/Phase)' },
                { key: 'quantity', title: 'Khối lượng', align: 'right' },
                { key: 'dates', title: 'Thời gian kế hoạch' },
                { key: 'resources', title: 'Giờ công dự kiến', align: 'right' },
              ]}
              data={items}
              renderCell={(item, key) => {
                switch (key) {
                  case 'name':
                    return (
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-900">{item.task_name}</span>
                        <span className="text-[10px] text-slate-400  font-bold tracking-tighter">{item.task_type}</span>
                      </div>
                    );
                  case 'location':
                    return (
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-700">{item.zone?.name || '---'}</span>
                        <span className="text-[10px] text-slate-400 italic">{item.phase?.name || '---'}</span>
                      </div>
                    );
                  case 'quantity':
                    return (
                      <div className="flex flex-col items-end">
                        <span className="text-sm font-bold text-slate-900">{item.planned_quantity}</span>
                        <span className="text-[10px] text-slate-400 font-bold ">{item.unit}</span>
                      </div>
                    );
                  case 'dates':
                    return (
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-700">{new Date(item.planned_start_date).toLocaleDateString('vi-VN')}</span>
                        <span className="text-[10px] text-slate-400">đến {new Date(item.planned_end_date).toLocaleDateString('vi-VN')}</span>
                      </div>
                    );
                  case 'resources':
                    return (
                      <div className="flex flex-col items-end">
                        <span className="text-xs font-bold text-slate-700">{item.estimated_man_hours} h</span>
                        <span className="text-[9px] text-slate-400 font-medium italic">Máy: {item.estimated_equipment_hours} h</span>
                      </div>
                    );
                  default:
                    return null;
                }
              }}
            />
          </Card>
        )}

        {(activeTab === 'resources' || activeTab === 'checklist' || activeTab === 'dependencies' || activeTab === 'schedule' || activeTab === 'work_orders') && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu đang được liên kết từ các phân hệ Tài nguyên dự kiến, Checklist sẵn sàng và Lịch trình thi công tự động."
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
